import React, { useState, useEffect } from "react";
import { Dropdown, Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetProjectsQuery, useGetCompaniesQuery } from "../../../store/appApi";
import { updateProjectApi } from "../../../services/project";

const theadData = [
  { heading: "Project Name", sortingVale: "name" },
  { heading: "Company", sortingVale: "category" },
  { heading: "City", sortingVale: "city" },
  { heading: "RERA Number", sortingVale: "" },
  { heading: "Total Area", sortingVale: "" },
  { heading: "Actions", sortingVale: "" },
];

const EMPTY_FORM = {
  _id: "",
  projectName: "",
  city: "",
  underCompany: "",
  reraNumber: "",
  totalProjectArea: "",
};

const AllProjects = () => {
  const dispatch = useDispatch();

  const [sort, setSort] = useState(10);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [iconData, setIconData] = useState({ complete: false, ind: -1 });

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // GET projects — RTK Query (Redux)
  const {
    data: projectsResponse,
    isLoading,
    isFetching,
  } = useGetProjectsQuery({
    page,
    limit: sort,
    search: debouncedSearch,
    sortBy,
    sortOrder,
  });

  // GET companies for the edit-form dropdown — RTK Query (Redux)
  const { data: companiesResponse } = useGetCompaniesQuery({ page: 1, limit: 100 });
  const companies = Array.isArray(companiesResponse?.data) ? companiesResponse.data : [];

  const rawList = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
  const pagination = projectsResponse?.pagination || {};
  const total = Number(pagination.total) || rawList.length;
  const totalPages = Number(pagination.totalPages) || 1;

  const projects = rawList.map((item, index) => ({
    id: item._id || item.id || String(index),
    name: item.name || "-",
    category: item.category || "-",
    underCompanyId: item.underCompanyId || "",
    city: item.city || "-",
    reraNumber: item.reraNumber || "-",
    totalProjectArea: item.totalProjectArea || "-",
  }));

  const showingFrom = total > 0 ? (page - 1) * sort + 1 : 0;
  const showingTo = total > 0 ? Math.min(page * sort, total) : 0;
  const pageNumbers = Array(totalPages).fill().map((_, i) => i + 1);
  const showLoading = isLoading || isFetching;

  const onSort = (sortingVale, ind) => {
    if (!sortingVale) return;
    const nextOrder = sortBy === sortingVale && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(sortingVale);
    setSortOrder(nextOrder);
    setPage(1);
    setIconData({ complete: nextOrder === "asc", ind });
  };

  const openEdit = (project) => {
    setEditForm({
      _id: project.id,
      projectName: project.name === "-" ? "" : project.name,
      city: project.city === "-" ? "" : project.city,
      underCompany: project.underCompanyId,
      reraNumber: project.reraNumber === "-" ? "" : project.reraNumber,
      totalProjectArea: project.totalProjectArea === "-" ? "" : project.totalProjectArea,
    });
    setEditErrors({});
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.projectName.trim()) e.projectName = "Project Name is required.";
    if (!editForm.city.trim()) e.city = "City is required.";
    return e;
  };

  // PUT: direct Axios call (no Redux)
  const handleUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = validateEdit();
    if (Object.keys(validationErrors).length) {
      setEditErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      await updateProjectApi({
        _id: editForm._id,
        projectName: editForm.projectName.trim(),
        city: editForm.city.trim(),
        reraNumber: editForm.reraNumber.trim(),
        totalProjectArea: editForm.totalProjectArea.trim(),
        underCompany: editForm.underCompany || undefined,
      });
      dispatch(appApi.util.invalidateTags(["Projects"]));
      setEditModal(false);
      Swal.fire({ icon: "success", title: "Updated!", text: "Project updated successfully." });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update project.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="All Projects" motherMenu="Projects" />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">All Projects</h4>
          <Link to="/add-project" className="btn btn-primary">
            + Add New
          </Link>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <div className="dataTables_wrapper no-footer">
              <div className="justify-content-between d-sm-flex">
                <div className="dataTables_length">
                  <label className="d-flex align-items-center">
                    Show
                    <Dropdown className="search-drop">
                      <Dropdown.Toggle as="div" className="search-drop-btn">
                        {sort}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {[10, 20, 30].map((n) => (
                          <Dropdown.Item key={n} onClick={() => { setSort(n); setPage(1); }}>
                            {n}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    entries
                  </label>
                </div>
                <div className="dataTables_filter">
                  <label>
                    Search:{" "}
                    <input
                      type="search"
                      className="form-control form-control-sm d-inline-block w-auto"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </label>
                </div>
              </div>

              <table className="display dataTable no-footer w-100">
                <thead>
                  <tr>
                    {theadData.map((item, ind) => (
                      <th
                        key={ind}
                        onClick={() => onSort(item.sortingVale, ind)}
                        style={item.sortingVale ? { cursor: "pointer" } : {}}
                      >
                        {item.heading}
                        {item.sortingVale && (
                          <span>
                            {ind !== iconData.ind && (
                              <i className="fa fa-sort ms-2 fs-12" style={{ opacity: "0.3" }} />
                            )}
                            {ind === iconData.ind && (
                              iconData.complete
                                ? <i className="fa fa-arrow-down ms-2 fs-12" style={{ opacity: "0.7" }} />
                                : <i className="fa fa-arrow-up ms-2 fs-12" style={{ opacity: "0.7" }} />
                            )}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showLoading && (
                    <tr><td colSpan={6}>Loading projects...</td></tr>
                  )}
                  {!showLoading && projects.length === 0 && (
                    <tr><td colSpan={6}>No projects found.</td></tr>
                  )}
                  {!showLoading && projects.map((data, ind) => (
                    <tr key={data.id || ind}>
                      <td style={{ textTransform: "capitalize" }}>{data.name}</td>
                      <td style={{ textTransform: "capitalize" }}>{data.category}</td>
                      <td style={{ textTransform: "capitalize" }}>{data.city}</td>
                      <td>{data.reraNumber}</td>
                      <td>{data.totalProjectArea}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-primary"
                          onClick={() => openEdit(data)}
                        >
                          <i className="fa fa-edit me-1" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                <div className="dataTables_info">
                  Showing {showingFrom} to {showingTo} of {total} entries
                </div>
                <div
                  className="dataTables_paginate paging_simple_numbers"
                  id="projects_paginate"
                >
                  <Link
                    className={`paginate_button previous ${page <= 1 ? "disabled" : ""}`}
                    to="#"
                    onClick={() => page > 1 && setPage(page - 1)}
                  >
                    Previous
                  </Link>
                  <span>
                    {pageNumbers.map((number, i) => (
                      <Link
                        key={i}
                        to="#"
                        className={`paginate_button ${page === number ? "current" : ""}`}
                        onClick={() => setPage(number)}
                      >
                        {number}
                      </Link>
                    ))}
                  </span>
                  <Link
                    className={`paginate_button next ${page >= totalPages ? "disabled" : ""}`}
                    to="#"
                    onClick={() => page < totalPages && setPage(page + 1)}
                  >
                    Next
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Project Modal */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="edit-project-form" onSubmit={handleUpdate}>

            <div className="mb-3">
              <label className="form-label">Select Company</label>
              <select
                name="underCompany"
                className="form-control"
                value={editForm.underCompany}
                onChange={handleEditChange}
              >
                <option value="">Select a company...</option>
                {companies.map((company) => (
                  <option
                    key={company._id || company.id}
                    value={company._id || company.id}
                  >
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Project Name <span className="text-danger">*</span>
              </label>
              <input
                name="projectName"
                type="text"
                className={`form-control ${editErrors.projectName ? "is-invalid" : ""}`}
                value={editForm.projectName}
                onChange={handleEditChange}
              />
              {editErrors.projectName && (
                <div className="invalid-feedback">{editErrors.projectName}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">
                City <span className="text-danger">*</span>
              </label>
              <input
                name="city"
                type="text"
                className={`form-control ${editErrors.city ? "is-invalid" : ""}`}
                value={editForm.city}
                onChange={handleEditChange}
              />
              {editErrors.city && (
                <div className="invalid-feedback">{editErrors.city}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">RERA Number</label>
              <input
                name="reraNumber"
                type="text"
                className="form-control"
                value={editForm.reraNumber}
                onChange={handleEditChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Total Project Area</label>
              <input
                name="totalProjectArea"
                type="text"
                className="form-control"
                value={editForm.totalProjectArea}
                onChange={handleEditChange}
              />
            </div>

          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="edit-project-form"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllProjects;
